import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Typography, CircularProgress, Divider, Chip,
} from '@material-ui/core';
import { CloseRounded } from '@material-ui/icons';
import { getIntegrationNotesForLead } from '../../Actions';

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
};

// Read-only view of a synced customer's notes from whichever external system
// they came from — the server resolves that from the lead. Fetched live each time the
// dialog opens. Nothing is stored locally: these are notes staff may act on, and
// showing a cached copy as if it were current is worse than showing nothing.
const ViewIntegrationNotesModal = ({ isOpen, onClose, lead }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!isOpen || !lead?.lead_id) return;

    setLoading(true);
    setError(null);
    setProjects([]);

    getIntegrationNotesForLead(lead.lead_id)
      .then((res) => setProjects(res.data?.data?.projects ?? []))
      .catch((err) =>
        setError(err?.response?.data?.message ?? 'Could not load notes from the connected system')
      )
      .finally(() => setLoading(false));
  }, [isOpen, lead?.lead_id]);

  const noteCount = projects.reduce((n, p) => n + (p.notes?.length ?? 0), 0);

  return (
    <Dialog open={!!isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        CRM Notes — {lead?.name}
        <CloseRounded style={{ float: 'right', cursor: 'pointer' }} onClick={onClose} />
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress size={24} />
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !projects.length ? (
          <Typography color="textSecondary">
            No projects are linked to this contact yet. Links are built by the
            scheduled sync — if this contact is new, try again after the next run.
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 12 }}>
              {noteCount} note{noteCount === 1 ? '' : 's'} across {projects.length} project
              {projects.length === 1 ? '' : 's'}, read live from the connected system.
            </Typography>

            {projects.map((project) => (
              <div key={project.project_id} style={{ marginBottom: 24 }}>
                <Typography variant="h6" style={{ display: 'inline-block', marginRight: 8 }}>
                  {project.project_name || `Project ${project.project_id}`}
                </Typography>
                {project.project_status ? (
                  <Chip size="small" label={project.project_status} style={{ marginRight: 6 }} />
                ) : undefined}
                {project.project_closed ? (
                  <Chip size="small" label="Closed" style={{ backgroundColor: '#00000017' }} />
                ) : undefined}

                <Divider style={{ margin: '8px 0 12px' }} />

                {project.error ? (
                  <Typography variant="body2" color="error">
                    Could not load notes for this project: {project.error}
                  </Typography>
                ) : !project.notes?.length ? (
                  <Typography variant="body2" color="textSecondary">No notes on this project.</Typography>
                ) : (
                  project.notes.map((note) => (
                    <div key={note.id} style={{ marginBottom: 14 }}>
                      <Typography variant="caption" color="textSecondary">
                        {formatWhen(note.created_at)}
                        {/* Albi marks notes internal or customer-visible. Staff
                            need to know which before repeating one aloud. */}
                        {note.access ? ` · ${note.access}` : ''}
                      </Typography>
                      <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                        {note.text}
                      </Typography>
                    </div>
                  ))
                )}
              </div>
            ))}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewIntegrationNotesModal;
